import React, { useEffect, useRef, useState } from "react";
import { Copy, Check, Code2, Download, Image as ImageIcon } from "lucide-react";

let mermaidP = null;
// htmlLabels off → labels are plain SVG <text>, so the diagram exports to PNG via canvas in every browser (foreignObject taints canvases on Safari/Firefox).
const loadMermaid = () => (mermaidP ||= import("mermaid").then((m) => {
  m.default.initialize({ startOnLoad: false, securityLevel: "strict", theme: "neutral", fontFamily: "inherit", htmlLabels: false, flowchart: { htmlLabels: false } });
  return m.default;
}));
let seq = 0;

const saveBlob = (blob, name) => { const u = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = u; a.download = name; document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(u), 1000); };

const svgSize = (el) => {
  const vb = el.viewBox?.baseVal;
  const r = el.getBoundingClientRect();
  return { w: Math.ceil(vb?.width || r.width || 800), h: Math.ceil(vb?.height || r.height || 400) };
};
const serialize = (el) => {
  const { w, h } = svgSize(el);
  const c = el.cloneNode(true);
  c.setAttribute("xmlns", "http://www.w3.org/2000/svg"); c.setAttribute("width", w); c.setAttribute("height", h); c.style.maxWidth = "";
  c.style.fontFamily = getComputedStyle(el).fontFamily || "sans-serif";
  return { xml: new XMLSerializer().serializeToString(c), w, h };
};

// ```mermaid fences → rendered diagram (lazy-loaded library); invalid syntax falls back to the raw code with a notice.
export function MermaidBlock({ code }) {
  const [svg, setSvg] = useState("");
  const [error, setError] = useState("");
  const [raw, setRaw] = useState(false);
  const [copied, setCopied] = useState(false);
  const host = useRef(null);
  useEffect(() => {
    let alive = true;
    setSvg(""); setError("");
    loadMermaid().then(async (mermaid) => {
      try {
        await mermaid.parse(code);
        const { svg: out } = await mermaid.render(`mmd-${Date.now()}-${seq++}`, code);
        if (alive) setSvg(out);
      } catch (e) {
        if (alive) setError(String(e?.message || e).split("\n")[0].slice(0, 160));
      }
    }).catch((e) => alive && setError("Mermaid gagal dimuat: " + String(e?.message || e).slice(0, 80)));
    return () => { alive = false; };
  }, [code]);
  const copy = async () => { try { await navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ } };
  const exportSvg = () => { const el = host.current?.querySelector("svg"); if (!el) return; saveBlob(new Blob([serialize(el).xml], { type: "image/svg+xml;charset=utf-8" }), "diagram.svg"); };
  const exportPng = async () => {
    const el = host.current?.querySelector("svg"); if (!el) return;
    const { xml, w, h } = serialize(el);
    const url = URL.createObjectURL(new Blob([xml], { type: "image/svg+xml;charset=utf-8" }));
    try {
      const img = new Image();
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
      const scale = 2, canvas = document.createElement("canvas");
      canvas.width = w * scale; canvas.height = h * scale;
      const ctx = canvas.getContext("2d"); ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, canvas.width, canvas.height); ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((b) => b && saveBlob(b, "diagram.png"), "image/png");
    } catch { exportSvg(); } finally { URL.revokeObjectURL(url); }
  };
  const ready = !!svg && !error;
  const btn = "flex items-center gap-1 hover:text-slate-800";
  return (
    <div className="my-2 overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="mermaid-block">
      <div className="flex items-center justify-between border-b border-slate-100 px-2.5 py-1 text-[11px] text-slate-500">
        <span>Diagram{error ? " · sintaks tidak valid, menampilkan kode" : ""}</span>
        <span className="flex flex-wrap gap-2">
          {ready && <button type="button" onClick={exportPng} className={btn} title="Unduh sebagai PNG" data-testid="mermaid-export-png"><ImageIcon size={11} /> PNG</button>}
          {ready && <button type="button" onClick={exportSvg} className={btn} title="Unduh sebagai SVG" data-testid="mermaid-export-svg"><Download size={11} /> SVG</button>}
          {ready && <button type="button" onClick={() => setRaw((r) => !r)} className={btn} data-testid="mermaid-toggle-raw"><Code2 size={11} /> {raw ? "Diagram" : "Kode"}</button>}
          <button type="button" onClick={copy} className={btn} data-testid="mermaid-copy">{copied ? <Check size={11} /> : <Copy size={11} />} {copied ? "Tersalin" : "Salin kode"}</button>
        </span>
      </div>
      {ready
        ? <div ref={host} className={`overflow-x-auto p-3 [&_svg]:mx-auto [&_svg]:max-w-full ${raw ? "hidden" : ""}`} dangerouslySetInnerHTML={{ __html: svg }} data-testid="mermaid-svg" />
        : null}
      {(!ready || raw) && <pre className="m-0 overflow-x-auto p-3 text-xs" data-testid="mermaid-raw"><code>{code}</code></pre>}
      {!svg && !error && <p className="px-3 pb-2 text-[11px] text-slate-400">Merender diagram…</p>}
    </div>
  );
}
